import moment from 'moment';
import axios from 'axios';
import { filter, find, get, includes, map, mapValues, split, trim, unionBy, uniq, upperCase } from 'lodash';
import { useSelector } from 'react-redux';
import {
  INITIALIZE_SUCCESS, INITIALIZE_FAILURE, INITIALIZE_REQUEST,
  FETCH_RECORDS_REQUEST, FETCH_RECORDS_FAILURE, FETCH_RECORDS_SUCCESS,
  SET_PERIOD, SET_CANCEL_TOKEN,
  IN_OUT_FILTERS,
} from './constants';
import { getEmployeeDetailOrg, getInOutEntries, getOrgCompanyUnits, getXapValue } from './api';
import { custom } from '../../utils/Api';
// import { fetchEmployeeDetailOrg, fetchEmployeeMaster } from '../../actions/profile';
import { getClockCardEntryTypes } from '../time/api';
import { getSettingByKey } from '../../utils/localSettings';

const getDates = (startDate, endDate, lang) => {
  const dateArray = [];
  const currentDate = new Date(startDate);
  moment.locale(lang);
  while (currentDate <= endDate) {
    dateArray.push({
      dateString: moment(currentDate).format('DD/MM/YY'),
      date: moment(currentDate),
      isWeekend: currentDate.getDay() === 6 || currentDate.getDay() === 0,
      dayOfWeek: moment(currentDate).format('ddd'),
    });
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return dateArray;
};

const setCancelToken = cancelToken => ({
  type: SET_CANCEL_TOKEN,
  cancelToken,
});

export const setPeriod = (start, end, lang) => ({
  type: SET_PERIOD,
  start,
  end,
  datesBetween: getDates(start, end, lang),
});

export const fetchEntriesRequest = () => ({
  type: FETCH_RECORDS_REQUEST,
});

export const fetchEntriesSuccess = (employees, clockTypes) => ({
  type: FETCH_RECORDS_SUCCESS,
  employees,
  clockTypes,
});

export const fetchEntriesFailure = error => ({
  type: FETCH_RECORDS_FAILURE,
  error,
});

export const requestEntries = (start, end, unitId) => async (dispatch, getState) => {
  const {
    inOutReducer: { cancelToken },
    languageReducer: { language },
  } = getState();
  if (cancelToken) {
    cancelToken.cancel();
  }
  try {
    const { CancelToken } = axios;
    const source = CancelToken.source();
    dispatch(setCancelToken(source));
    dispatch(fetchEntriesRequest());
    const employeesRes = await custom(getInOutEntries(start, end, unitId, source.token));
    const employees = get(employeesRes, 'data.value', []);
    const clockCardTypes = [];
    map(employees, emp => (
      map(emp.ClockCardEntries, (clock) => {
        if (clock.Active && clock.ClockCardEntryTypeName) {
          clockCardTypes.push({
            id: clock.ClockCardEntryTypeName,
            value: language === 'is' ? clock.ClockCardEntryTypeName : clock.ClockCardEntryTypeName,
          });
        }
      })));
    const clockTypes = unionBy(clockCardTypes, 'id');
    dispatch(setCancelToken(null));
    dispatch(fetchEntriesSuccess(employees, clockTypes));
  } catch (error) {
    if (!axios.isCancel(error)) {
      dispatch(fetchEntriesFailure(error));
    }
  }
};

export const changePeriod = (start, end, unitIds) => async (dispatch, getState) => {
  const {
    languageReducer: { language },
  } = getState();

  let ids;
  // if unitIds is [null], then we want to get all units by setting ids to null
  if (unitIds.length === 1 && unitIds[0] === null) {
    ids = null;
  } else {
    ids = unitIds;
  }
  dispatch(setPeriod(start.startOf('day'), end.endOf('day'), language));
  dispatch(requestEntries(start.startOf('day'), end.endOf('day'), ids));
};

export const initializeRequest = () => ({
  type: INITIALIZE_REQUEST,
});

export const initializeSuccess = (periodFrom, orgCompanyUnits, defaultCompanyUnitID, clockTypesById, inOutFilters) => ({
  type: INITIALIZE_SUCCESS,
  periodFrom,
  orgCompanyUnits,
  defaultCompanyUnitID,
  clockTypesById,
  inOutFilters
});

export const initializeFailure = error => ({
  type: INITIALIZE_FAILURE,
  error,
});

export const initialize = () => async (dispatch, getState) => {
  const {
    languageReducer: { language },
    WebValuesReducer: { valuesByKey },
    loginReducer: { user: { EmployeeMasterID, DefaultEmployeeDetailID } },
    profileReducer: { employeeDetailOrgList },
  } = getState();
  try {
    dispatch(initializeRequest());
    const requests = [
      custom(getXapValue('Pay.PeriodFrom')),
    ];
    const [xapRes] = await Promise.all(requests);
    const periodFrom = get(xapRes, 'data.value[0].Value') || '1';
    let startDate = moment().date(periodFrom).add(-1, 'months').startOf('day');
    let endDate = moment().date(periodFrom - 1).endOf('day');
    if (endDate.isSameOrBefore(moment())) {
      startDate = startDate.add(1, 'months');
      endDate = startDate.clone().add(1, 'months').add(-1, 'days').endOf('day');
    }

    let defaultCompanyUnitID;
    if (!employeeDetailOrgList || !employeeDetailOrgList.length) {
      const employeeDetailOrgs = await get(await custom(getEmployeeDetailOrg(EmployeeMasterID)), 'data');
      defaultCompanyUnitID = get(find(employeeDetailOrgs, unit => unit.Entity.EmployeeDetailID === DefaultEmployeeDetailID), 'Entity.OrgCompanyUnitID');
    } else {
      defaultCompanyUnitID = get(find(employeeDetailOrgList, unit => unit.EmployeeDetailID === DefaultEmployeeDetailID), 'OrgCompanyUnitID');
    }

    let orgCompanyUnits = [];
    const customOrgCompanyUnitsWebValue = get(valuesByKey, ['InOut.OrgCompanyUnitsIDs', 'Value']);
    const allOrgCompanyUnits = get(await custom(getOrgCompanyUnits()), 'data.value', []);

    if (customOrgCompanyUnitsWebValue) {
      const customOrgCompanyUnitsIds = map(split(get(valuesByKey, ['InOut.OrgCompanyUnitsIDs', 'Value']), ','), id => parseInt(trim(id), 10));
      // we need to add defaultCompanyUnitID to the list if it is not there
      customOrgCompanyUnitsIds.push(defaultCompanyUnitID);
      orgCompanyUnits = filter(allOrgCompanyUnits, unit => includes(uniq(customOrgCompanyUnitsIds), unit.OrgCompanyUnitID));
    } else {
      orgCompanyUnits = allOrgCompanyUnits;
    }

    const clockTypes = get(await custom(getClockCardEntryTypes), 'data.value', []);
    let i = 1;
    let j = 0;

    const allColors = ['#3B53A7', '#77BB97', '#A07CEF', '#F7A548', '#6D85C6', '#8F36AA', '#419488', '#52BAD1', '#97C05C', '#667C89', '#9E9E9E', '#5E36B3', '#93382C', '#FBAEA7'];
    const clockTypesById = clockTypes.reduce((acc, elem) => {
      if (elem.ClockCardEntryTypeRule === 'Daytime') {
        let shortKey = upperCase(elem.ClockCardEntryTypeName.slice(0, 2)).replace(/ /g, '');
        mapValues(acc, (item) => {
          const shortKeyExists = Object.values(item).includes(shortKey);
          if (shortKeyExists) {
            shortKey = `${elem.ClockCardEntryTypeName.slice(0, 1)}${i}`;
            i += 1;
          }
        });
        acc[elem.ClockCardEntryTypeName] = {
          id: elem.ClockCardEntryTypeName,
          color: allColors[j % (allColors.length - 1)],
          value: language === 'is' ? elem.ClockCardEntryTypeName : elem.ClockCardEntryTypeName,
          value_en: elem.ClockCardEntryTypeName,
          value_short: shortKey,
        };
        j += 1;
      }
      return acc;
    }, {});
    const inOutFilters = getSettingByKey(IN_OUT_FILTERS) || {
      orgCompanyUnitId: [defaultCompanyUnitID],
      employeeId: [],
      typeId: [],
    };
    let units = get(inOutFilters, 'orgCompanyUnitId', [defaultCompanyUnitID]);

    units = units.includes(null) ? map(orgCompanyUnits, 'OrgCompanyUnitID') : units;
    dispatch(initializeSuccess(periodFrom, orgCompanyUnits, defaultCompanyUnitID, clockTypesById, inOutFilters));
    dispatch(changePeriod(startDate, endDate, units));
  } catch (error) {
    dispatch(initializeFailure(get(error, 'message')));
  }
};

export const useInOutState = () => useSelector(state => state.inOutReducer);



// WEBPACK FOOTER //
// ./src/sites/inOut/actions.js